/**
 * ContinueProcurement Page - Resume an existing procurement
 * Refactored: Uses shared WizardContext and step components from NewProcurement
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { toastSuccess, toastError } from '@/lib/toast';
import {
  WizardProvider,
  useWizard,
  WIZARD_STEPS,
  WizardHeader,
  StepIntake,
  StepQuestions,
  StepAnswers,
  StepReview,
  StepLineItems,
  StepRfpPreview,
  StepRisk,
  WizardMasterReferenceSurfaces
} from '@/components/wizard';

// Inner component that uses the wizard context
const ContinueProcurementContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [initialLoading, setInitialLoading] = useState(true);
  const {
    currentStep,
    loadProcurement,
    setUserRequirement,
    setParsedRequirement,
    setBusinessQuestions,
    setVendorQuestions,
    setAnswers,
    setRefinedRequirement,
    setLineItems,
    setRfpDocument,
    setRiskAssessment,
    setBoqItems,
    setAttachedDocuments,
    setCurrentStep
  } = useWizard();

  useEffect(() => {
    // ProcurementDetail's "Edit" button fix (2026-07-26): a transient
    // router navigation-state signal, read once and then stripped from
    // this history entry (via the replace below) so a refresh of this
    // same /continue/{id} URL never re-applies it - only the ORIGINAL
    // navigation from the Edit button carries it. Does not unlock or
    // bypass anything: it only tells loadProcurement which step to
    // render on top of the procurement's real (unchanged) progress, so
    // StepIntake's own read-only view + confirmation-gated Edit trigger
    // still make the actual decision.
    const targetStep = location.state?.targetStep;
    const forceStep = targetStep === 'intake'
      ? WIZARD_STEPS.findIndex(s => s.id === 'intake')
      : undefined;

    if (targetStep) {
      navigate(location.pathname, { replace: true });
    }

    const loadData = async () => {
      try {
        const { resumeStep } = await loadProcurement(id, { forceStep });

        // loadProcurement (WizardContext, G-9) already seeds editableQuestions
        // with the correct priority - final_vendor_questions, then
        // revised_vendor_questions, then the AI originals. D-13: this effect
        // used to re-set it afterwards preferring revised_vendor_questions
        // over final_vendor_questions whenever revised was non-empty, which
        // clobbered a user's Review-step vendor-question addition with the
        // pre-answer revised list on every resume. Don't touch it here.

        // forceStep lands the user on Intake regardless of the real resume
        // step - "Resuming from Answers step" would contradict what's on
        // screen, so this toast is specific to the normal (unsignaled) path.
        if (forceStep === undefined) {
          toastSuccess(`Resuming from ${WIZARD_STEPS[resumeStep]?.label || 'saved'} step`);
        }
      } catch (error) {
        console.error('Failed to load procurement:', error);
        toastError('Failed to load procurement. It may not exist or you may not have access.');
        navigate('/');
      } finally {
        setInitialLoading(false);
      }
    };

    if (id) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, loadProcurement, navigate]);

  if (initialLoading) {
    return (
      <div className="p-6 md:p-12">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#0055FF] mx-auto mb-4" />
            <p className="text-zinc-600">Loading procurement...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render appropriate step based on currentStep
  // POSITIONAL AND INDEX-ALIGNED TO WIZARD_STEPS. This array is a
  // hand-maintained duplicate of the one in the other wizard page; nothing
  // used to assert that either of them matched WIZARD_STEPS, which is why
  // stepModel.parity.test.jsx now does. If you add or remove a step, three
  // places change together: WIZARD_STEPS, this array, and its twin.
  const steps = [
    <StepIntake key="intake" />,
    <StepQuestions key="questions" />,
    <StepAnswers key="answers" />,
    <StepReview key="review" />,
    <StepLineItems key="items" />,
    <StepRfpPreview key="rfp" />,
    <StepRisk key="risk" />
  ];

  return (
    <>
      <WizardHeader title="Continue Procurement" />

      {/* Item 3 (layout correction) + UI CONSISTENCY (2026-07-27): same
          fix as NewProcurement.jsx, kept in sync (this page renders the
          identical wizard chrome for a resumed procurement) - max-w-6xl
          is the single choke point for step content width, see that
          file's comment for the full rationale. */}
      <div className="px-4 pb-6 md:px-8 md:pb-12 pt-6">
        <div className="bg-white border border-zinc-200 p-4 md:p-6 max-w-6xl mx-auto">
          {/* Staleness gate: advisory + risk fork, above every step. Same
              placement in NewProcurement.jsx - keep the two in sync. */}
          <WizardMasterReferenceSurfaces />
          {steps[currentStep] || null}
        </div>
      </div>
    </>
  );
};

const ContinueProcurement = () => {
  const { id } = useParams();
  // STALE QUOTA PANEL FIX (2026-07-28 ruling): same wiring as
  // NewProcurement.jsx - see WizardContext.jsx's onUsageChanged docstring.
  const { checkAuth } = useAuth();

  return (
    <WizardProvider initialProcurementId={id} onUsageChanged={checkAuth}>
      <Layout>
        <ContinueProcurementContent />
      </Layout>
    </WizardProvider>
  );
};

export default ContinueProcurement;
